import { IAPIArrayData } from '@shared/interfaces/api.interface';
import { IFilter } from '@shared/interfaces/filter.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { IPagination } from '@shared/interfaces/pagination.interface';
import { ISort } from '@shared/interfaces/sort.interface';
import { Pagination } from '@shared/models/pagination.model';
import { LoggerService } from '@shared/services/logger.service';
import { BehaviorSubject, Observable } from 'rxjs';

export class PaginationService<T> {
  private static readonly PAGE_SIZE: number = Number(10);

  public _pageBehavior: BehaviorSubject<IPagination> = new BehaviorSubject<IPagination>(
    new Pagination<T>({})
  );

  public _totalEntriesBehavior: BehaviorSubject<number> = new BehaviorSubject<number>(
    0
  );

  public page: Observable<IPagination> = this._pageBehavior.asObservable();
  public entries: Observable<number> = this._totalEntriesBehavior.asObservable();

  public totalPagesCount: number;
  public totalEntriesCount: number;
  public currentPage: number;
  public pageSize: number;

  public pages: Map<number, IPagination> = new Map<number, IPagination>();

  private _dates: any;
  private _filter: any;
  private _lastRequestedPageNumber = Number(0);
  private _sort: any;

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _paginable: IPaginable,
    private readonly _logger: LoggerService
  ) {
    this.onGetRawData = this.onGetRawData.bind(this);

    this.totalPagesCount = 0;
    this.totalEntriesCount = 0;
    this.currentPage = 1;
    this.pageSize = PaginationService.PAGE_SIZE;
  }

  public checkAndLoadPage(pageNumber: number) {
    this._lastRequestedPageNumber = pageNumber;

    if (this.pages.has(pageNumber)) {
      this.setPage(this.pages.get(pageNumber) as any);
    } else {
      const page = new Pagination<T>({ page: pageNumber });
      this.setPage(page);
      this.loadPage(pageNumber);
    }
  }

  public fullReload() {
    this.clear();
    this.checkAndLoadPage(1);
  }

  public getDates(): IFilter[] {
    return this._dates;
  }

  public getFilter(): IFilter {
    return this._filter;
  }

  public getSort(): ISort {
    return this._sort;
  }

  public reloadPage(pageNumber: number) {
    if (this.pages.has(pageNumber)) {
      this.pages.delete(pageNumber);
    }

    const page = new Pagination<T>({ page: pageNumber });
    this.setPage(page);
    this._lastRequestedPageNumber = pageNumber;
    this.loadPage(pageNumber);
  }

  // does the same as clear(),
  // but is extended in some subclasses for reseting the filter values
  public reset() {
    this.clear();
  }

  public setDates(dates: IFilter[]) {
    this._dates = dates;
  }

  public setFilter(filter: IFilter) {
    this._filter = filter;
  }

  public setSort(sort: ISort) {
    this._sort = sort;
  }

  // clears all the stored data on pages
  private clear() {
    this.pages = new Map<number, IPagination>();

    this.totalPagesCount = 0;
    this.totalEntriesCount = 0;
    this.currentPage = 1;
    this.pageSize = PaginationService.PAGE_SIZE;
  }

  private loadPage(pageNumber: number) {
    const url = `GET page ${pageNumber}`;
    this._paginable
      .getPage(
        pageNumber,
        this.onGetRawData,
        this._filter,
        this._sort,
        this._dates
      )
      .subscribe(
        (res: IPagination[]) => {
          const page = new Pagination<T>({
            page: pageNumber,
            isLoading: false,
            entries: res
          });

          this._logger.info(this.constructorName, url, page.entries);

          this.pages.set(pageNumber, page);
          this.setPage(page);

          if (this._lastRequestedPageNumber === pageNumber) {
            this._lastRequestedPageNumber = 0;
          }
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
        }
      );
  }

  private onGetRawData(res: IAPIArrayData) {
    if (res.pagination) {
      this.totalPagesCount = res.pagination.total_pages;
      this.totalEntriesCount = res.pagination.total_entries;
      this.currentPage = res.pagination.current_page;
      this.pageSize = res.pagination.page_size;

      this._totalEntriesBehavior.next(this.totalEntriesCount);
    }
  }

  private setPage(page: IPagination): void {
    this.pages.set(page.page, page);

    if (this._lastRequestedPageNumber === page.page) {
      this._pageBehavior.next(page);
    }
  }
}
